import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';

const WALKTHROUGH_KEY = 'rocketvan_admin_walkthrough_done';

const steps = [
  {
    target: '[data-walkthrough="nav-dashboard"]',
    title: 'Dashboard',
    content: 'Your home base — a quick overview of leads, stats, and recent activity at a glance.',
  },
  {
    target: '[data-walkthrough="nav-leads"]',
    title: 'Leads',
    content: 'All incoming quote requests and bookings. Filter by service type, status, and search by customer details.',
  },
  {
    target: '[data-walkthrough="nav-crm"]',
    title: 'CRM',
    content: 'Track customers across their lifecycle — from first lead to loyal repeat customer. Add notes, tags, and follow-ups.',
  },
  {
    target: '[data-walkthrough="nav-workflows"]',
    title: 'Workflows',
    content: 'Set up automated triggers — like sending a WhatsApp when a new quote comes in, or a follow-up after delivery.',
  },
  {
    target: '[data-walkthrough="nav-message-queue"]',
    title: 'Message Queue',
    content: 'Pending WhatsApp messages ready to send. Click to open pre-filled WhatsApp links, then mark as sent.',
  },
  {
    target: '[data-walkthrough="nav-campaigns"]',
    title: 'Campaigns',
    content: 'Create promotions with discount codes, targeted audiences, and scheduling. Track campaign performance.',
  },
  {
    target: '[data-walkthrough="nav-reviews"]',
    title: 'Reviews',
    content: 'Manage customer reviews — approve, hide, or feature testimonials that appear on your public website.',
  },
  {
    target: '[data-walkthrough="nav-revenue"]',
    title: 'Revenue & Cohorts',
    content: 'Financial insights and customer segmentation. See revenue breakdowns, top customers, and cohort analysis.',
  },
  {
    target: '[data-walkthrough="nav-settings"]',
    title: 'Settings',
    content: 'Configure referral programs, partner discounts, loyalty tiers, communication preferences, and more.',
  },
];

const AdminWalkthrough = ({ activeView }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const done = localStorage.getItem(WALKTHROUGH_KEY);
    if (!done && activeView === 'dashboard') {
      const timer = setTimeout(() => setIsOpen(true), 1200);
      return () => clearTimeout(timer);
    }
  }, [activeView]);

  const positionTooltip = useCallback(() => {
    if (!isOpen || currentStep < 0) return;
    const step = steps[currentStep];
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    setTooltipPos({
      top: rect.top + rect.height / 2,
      left: rect.right + 16,
    });

    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [isOpen, currentStep]);

  useEffect(() => {
    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [positionTooltip]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleFinish = () => {
    localStorage.setItem(WALKTHROUGH_KEY, 'true');
    setIsOpen(false);
  };

  const handleSkip = () => {
    localStorage.setItem(WALKTHROUGH_KEY, 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const targetEl = document.querySelector(step?.target);
  const targetRect = targetEl?.getBoundingClientRect();

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998]" style={{ pointerEvents: 'auto' }}>
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
          <defs>
            <mask id="walkthrough-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 4}
                  y={targetRect.top - 4}
                  width={targetRect.width + 8}
                  height={targetRect.height + 8}
                  rx="8"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0" y="0" width="100%" height="100%"
            fill="rgba(0,0,0,0.6)"
            mask="url(#walkthrough-mask)"
            style={{ pointerEvents: 'auto' }}
            onClick={handleSkip}
          />
        </svg>

        {/* Spotlight ring */}
        {targetRect && (
          <div
            className="absolute border-2 border-[#039fff] rounded-lg pointer-events-none"
            style={{
              top: targetRect.top - 4,
              left: targetRect.left - 4,
              width: targetRect.width + 8,
              height: targetRect.height + 8,
              boxShadow: '0 0 0 4px rgba(3,159,255,0.25)',
              transition: 'all 0.3s ease',
            }}
          />
        )}

        {/* Tooltip */}
        <div
          className="absolute z-[9999] w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{
            top: Math.max(16, Math.min(tooltipPos.top - 80, window.innerHeight - 260)),
            left: Math.min(tooltipPos.left, window.innerWidth - 340),
            transition: 'top 0.3s ease, left 0.3s ease',
          }}
          data-testid="walkthrough-tooltip"
        >
          {/* Header */}
          <div className="bg-[#001A47] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-[#fb6402]" />
              <span className="text-white font-semibold text-sm">Admin Tour</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[#039fff] text-xs font-medium">{currentStep + 1}/{steps.length}</span>
              <button onClick={handleSkip} className="text-white/60 hover:text-white transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <h3 className="font-bold text-[#001A47] text-base mb-2">{step.title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{step.content}</p>
          </div>

          {/* Progress bar */}
          <div className="px-5 pb-1">
            <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#039fff] rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="px-5 py-3 flex items-center justify-between border-t border-gray-100">
            <button
              onClick={handleSkip}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrev}
                  className="h-8 text-xs"
                >
                  <ChevronLeft className="h-3 w-3 mr-1" />
                  Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className="h-8 text-xs bg-[#039fff] hover:bg-[#0380cc]"
              >
                {currentStep < steps.length - 1 ? (
                  <>
                    Next
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </>
                ) : (
                  "Got it!"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export { WALKTHROUGH_KEY };
export default AdminWalkthrough;
